import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/services/api";
import { outputKeys } from "@/features/outputs/outputsApi";
import type { Output } from "@/features/outputs/types";
import type {
  AdminSectionPrompt,
  OutlineItem,
  Section,
  SectionPatch,
  SectionPrompt,
  SectionPromptInput,
  SectionSourceInput,
  SectionSourcePatch,
} from "./types";

export const draftKeys = {
  sections: (outputId: string) => ["pubcreator", "sections", outputId] as const,
  prompts: () => ["pubcreator", "section-prompts"] as const,
  adminPrompts: () => ["pubcreator", "admin", "section-prompts"] as const,
};

export function useSections(outputId: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: draftKeys.sections(outputId),
    queryFn: () => api.get<Section[]>(`/api/outputs/${outputId}/sections`),
    enabled: !!outputId && enabled,
    // Open editors and inputs hold their own copy; someone else's save surfaces as a 409.
    refetchOnWindowFocus: false,
  });
}

export function useSectionPrompts({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: draftKeys.prompts(),
    queryFn: () => api.get<SectionPrompt[]>("/api/section-prompts"),
    staleTime: 5 * 60_000,
    enabled,
  });
}

/** Brief → First draft; creates the default outline the first time. */
export function useStartFirstDraft(outputId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Output>(`/api/outputs/${outputId}/first-draft`, {}),
    onSuccess: (output) => {
      queryClient.setQueryData(outputKeys.output(output.id), output);
      void queryClient.invalidateQueries({ queryKey: outputKeys.project(output.project_id) });
      void queryClient.invalidateQueries({ queryKey: draftKeys.sections(output.id) });
    },
  });
}

/** Mutations on one output's outline; each keeps the cached section list current. */
export function useOutlineMutations(outputId: string) {
  const queryClient = useQueryClient();
  const key = draftKeys.sections(outputId);
  const replace = (section: Section) =>
    queryClient.setQueryData<Section[]>(key, (sections) => sections?.map((s) => (s.id === section.id ? section : s)));
  const refetch = () => queryClient.invalidateQueries({ queryKey: key });

  return {
    add: useMutation({
      mutationFn: (body: { title: string; parent_id?: string | null }) => api.post<Section>(`/api/outputs/${outputId}/sections`, body),
      onSettled: refetch,
    }),
    update: useMutation({
      mutationFn: ({ id, patch }: { id: string; patch: SectionPatch }) => api.patch<Section>(`/api/sections/${id}`, patch),
      onSuccess: replace,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete<void>(`/api/sections/${id}`),
      onSettled: refetch,
    }),
    reorder: useMutation({
      mutationFn: (items: OutlineItem[]) => api.put<Section[]>(`/api/outputs/${outputId}/outline`, { items }),
      // Show the new order straight away, so a dropped row doesn't jump back while it saves.
      onMutate: async (items) => {
        await queryClient.cancelQueries({ queryKey: key });
        queryClient.setQueryData<Section[]>(key, (sections) => {
          const byId = new Map(sections?.map((s) => [s.id, s]));
          return items.flatMap((i) => {
            const s = byId.get(i.id);
            return s ? [{ ...s, parent_id: i.parent_id }] : [];
          });
        });
      },
      onSuccess: (sections) => queryClient.setQueryData(key, sections),
      onError: refetch,
    }),
    addSource: useMutation({
      mutationFn: ({ sectionId, source }: { sectionId: string; source: SectionSourceInput }) =>
        api.post<Section>(`/api/sections/${sectionId}/sources`, source),
      onSuccess: replace,
    }),
    updateSource: useMutation({
      mutationFn: ({ sectionId, sourceId, patch }: { sectionId: string; sourceId: string; patch: SectionSourcePatch }) =>
        api.patch<Section>(`/api/sections/${sectionId}/sources/${sourceId}`, patch),
      onSuccess: replace,
    }),
    removeSource: useMutation({
      mutationFn: ({ sectionId, sourceId }: { sectionId: string; sourceId: string }) =>
        api.delete<Section>(`/api/sections/${sectionId}/sources/${sourceId}`),
      onSuccess: replace,
    }),
    refetch,
  };
}

export type OutlineMutations = ReturnType<typeof useOutlineMutations>;

export function useAdminSectionPrompts() {
  return useQuery({
    queryKey: draftKeys.adminPrompts(),
    queryFn: () => api.get<AdminSectionPrompt[]>("/api/admin/section-prompts"),
  });
}

export function useSectionPromptMutations() {
  const queryClient = useQueryClient();
  // Writers' "choose from library" picker lists the same prompts.
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: draftKeys.adminPrompts() }),
      queryClient.invalidateQueries({ queryKey: draftKeys.prompts() }),
    ]);
  return {
    create: useMutation({
      mutationFn: (body: SectionPromptInput) => api.post<AdminSectionPrompt>("/api/admin/section-prompts", body),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: SectionPromptInput }) =>
        api.put<AdminSectionPrompt>(`/api/admin/section-prompts/${id}`, body),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete<void>(`/api/admin/section-prompts/${id}`),
      onSuccess: invalidate,
    }),
  };
}
